import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import DatePicker from "react-datepicker";
import PhoneInput from "react-phone-input-2";

import { Context } from "../../context/context";
import { Button, Field, TextInput } from "../ui";
import { Genders } from "../../utilities/constants";

const ProfileGeneral = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState(null);
  const [dob, setDob] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [abhaNumber, setAbhaNumber] = useState("");
  const [nameError, setNameError] = useState("");

  const { state, dispatch } = useContext(Context);
  const { user } = state;

  useEffect(() => {
    setDisplayName(user.displayName || "");
    setGender(user.gender);
    setDob(user.dob ? DateTime.fromISO(user.dob).setZone("local", { keepLocalTime: true }).toJSDate() : null);
    setPhoneNumber(user.phoneNumber);
    setAbhaNumber(user.ABHANumber || "");
  }, [user]);

  const handleSave = async () => {
    if (isLoading) return;
    if (!displayName.trim()) {
      setNameError("Name is required");
      return;
    }
    setNameError("");

    const payload = {
      displayName: displayName.trim(),
      gender: gender || null,
      dob: dob
        ? DateTime.fromJSDate(dob).set({ hour: 0, minute: 0, second: 0 }).setZone("utc", { keepLocalTime: true }).toISO()
        : null,
      phoneNumber: phoneNumber || null,
      ABHANumber: abhaNumber || null,
    };

    const unchanged =
      payload.displayName === user.displayName &&
      payload.gender === user.gender &&
      payload.dob === user.dob &&
      payload.phoneNumber === user.phoneNumber &&
      payload.ABHANumber === user.ABHANumber;

    if (unchanged) {
      toast.info("No changes made");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post("/app-api/user/update-profile", payload);
      dispatch({ type: "UPDATE_PROFILE", payload });
      toast.success("Profile details updated");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-lg">
      <Field label="Name" error={nameError} htmlFor="profile-name">
        <TextInput
          id="profile-name"
          type="text"
          value={displayName}
          invalid={Boolean(nameError)}
          placeholder="Enter your name"
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </Field>

      <Field label="Gender" hint="Only visible to you and your caregivers.">
        <div className="flex flex-wrap gap-2">
          {Genders.map((option) => (
            <label
              key={option}
              className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm transition ${
                gender === option
                  ? "border-primary-500 bg-primary-50 font-semibold text-primary-800"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="gender"
                value={option}
                checked={gender === option}
                onChange={(e) => setGender(e.target.value)}
              />
              {option}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Date of Birth" htmlFor="profile-dob">
        <DatePicker
          id="profile-dob"
          className="field-input"
          wrapperClassName="w-full"
          dateFormat="dd/MM/yyyy"
          placeholderText="DD/MM/YYYY"
          minDate={new Date("01/01/1900")}
          maxDate={new Date()}
          openToDate={dob ? undefined : new Date("01/01/2000")}
          showMonthDropdown
          showYearDropdown
          yearDropdownItemNumber={100}
          scrollableYearDropdown
          showPopperArrow={false}
          selected={dob}
          onChange={setDob}
        />
      </Field>

      <Field label="Mobile Number">
        <PhoneInput
          preferredCountries={["in"]}
          placeholder="Enter your mobile number"
          value={phoneNumber}
          onChange={setPhoneNumber}
          inputClass="!w-full !h-[42px] !rounded-lg !border-gray-200 !text-sm !text-primary-900"
          buttonClass="!border-gray-200 !bg-white !rounded-l-lg"
          containerClass="w-full"
        />
      </Field>

      <Field label="ABHA Number" htmlFor="profile-abha" hint="Optional — your Ayushman Bharat Health Account ID.">
        <TextInput
          id="profile-abha"
          type="text"
          value={abhaNumber}
          placeholder="Enter your ABHA number"
          onChange={(e) => setAbhaNumber(e.target.value)}
        />
      </Field>

      <Button onClick={handleSave} loading={isLoading}>
        Save
      </Button>
    </div>
  );
};

export default ProfileGeneral;
